import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, RefreshCw } from "lucide-react";
import { api, imgSrc } from "../lib/api";
import { PageHero } from "../components/PageHero";
import { FadeUp } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { Pagination } from "../components/Pagination";

const BERITA_PER_PAGE = 6;

const formatTanggal = (raw) => {
  const d = new Date(raw);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("id-ID", {
    day: "numeric", month: "long", year: "numeric",
  });
};

export default function BeritaHukum() {
  const [items, setItems] = useState(null);
  const [managed, setManaged] = useState([]);
  const [gagal, setGagal] = useState(false);
  const [beritaPage, setBeritaPage] = useState(1);
  const beritaRef = useRef(null);

  const beritaTotalPages = Math.max(1, Math.ceil(managed.length / BERITA_PER_PAGE));
  const beritaItems = managed.slice(
    (beritaPage - 1) * BERITA_PER_PAGE,
    beritaPage * BERITA_PER_PAGE
  );

  const goToBeritaPage = (n) => {
    setBeritaPage(n);
    beritaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const load = () => {
    setGagal(false);
    api
      .get("/news")
      .then((r) => setItems(r.data))
      .catch(() => { setItems([]); setGagal(true); });
    api.get("/berita").then((r) => setManaged(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const [featured, ...rest] = items || [];

  return (
    <>
      <PageHero
        eyebrow="Media — Berita Hukum"
        titleLines={[
          "Kabar hukum terkini",
          <span key="t" className="italic text-gold">
            dari seluruh Indonesia.
          </span>,
        ]}
        description="Kumpulan berita hukum daring yang diperbarui otomatis dari berbagai sumber tepercaya — agar Anda selalu tahu perkembangan hukum terbaru."
      />

      {managed.length > 0 && (
        <section ref={beritaRef} data-testid="berita-managed-section" className="mx-auto w-full max-w-7xl scroll-mt-28 px-6 pb-20 lg:px-10">
          <FadeUp>
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-gold/70" />
              <span className="text-[11px] font-medium uppercase tracking-[0.35em] text-gold">
                Berita dari YLBH
              </span>
            </div>
          </FadeUp>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {beritaItems.map((b, i) => {
              const Tag = b.link ? "a" : "div";
              const linkProps = b.link ? { href: b.link, target: "_blank", rel: "noopener noreferrer" } : {};
              return (
                <FadeUp key={b.id} delay={0.06 * (i % 3)}>
                  <Tag
                    {...linkProps}
                    data-testid={`berita-managed-${b.id}`}
                    className="group block h-full border border-white/10 bg-[#0d0d0d] transition-colors duration-500 hover:border-gold/40"
                  >
                    {b.gambar && (
                      <div className="overflow-hidden">
                        <img
                          src={imgSrc(b.gambar)}
                          alt={b.judul}
                          className="aspect-[16/9] w-full object-cover grayscale-[30%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-gold">
                        YLBH Sumatera Timur — {b.tanggal}
                      </p>
                      <h3 className="mt-3 font-serif text-xl leading-snug text-[#F5F5F5] transition-colors group-hover:text-gold-light lg:text-2xl">
                        {b.judul}
                      </h3>
                      {b.ringkasan && (
                        <p className="mt-3 text-sm font-light leading-relaxed text-[#A3A3A3]">
                          {b.ringkasan}
                        </p>
                      )}
                      {b.link && (
                        <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">
                          Baca selengkapnya
                          <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </span>
                      )}
                    </div>
                  </Tag>
                </FadeUp>
              );
            })}
          </div>
          <Pagination
            page={beritaPage}
            totalPages={beritaTotalPages}
            onChange={goToBeritaPage}
            testidPrefix="berita"
          />
        </section>
      )}

      <section className="mx-auto w-full max-w-7xl px-6 pb-28 lg:px-10">
        {managed.length > 0 && (
          <FadeUp className="mb-10">
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-gold/70" />
              <span className="text-[11px] font-medium uppercase tracking-[0.35em] text-gold">
                Himpunan Berita Daring
              </span>
            </div>
          </FadeUp>
        )}
        {items === null ? (
          <p data-testid="berita-loading" className="py-20 text-center font-serif text-2xl italic text-[#737373]">
            Memuat berita…
          </p>
        ) : gagal || items.length === 0 ? (
          <div data-testid="berita-gagal" className="border border-white/10 bg-[#0d0d0d] px-8 py-20 text-center">
            <p className="font-serif text-2xl italic text-[#F5F5F5]">Berita belum dapat dimuat.</p>
            <p className="mx-auto mt-3 max-w-md text-sm font-light text-[#A3A3A3]">
              Sambungan ke sumber berita sedang terganggu. Silakan coba beberapa saat lagi.
            </p>
            <button
              onClick={load}
              data-testid="berita-coba-lagi"
              className="mt-6 inline-flex items-center gap-2 border border-gold/50 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-ink"
            >
              <RefreshCw size={14} /> Coba Lagi
            </button>
          </div>
        ) : (
          <>
            {featured && (
              <FadeUp>
                <a
                  href={featured.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="berita-featured"
                  className="group block border border-gold/30 bg-[#0d0d0d] p-8 transition-colors duration-500 hover:border-gold lg:p-12"
                >
                  <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
                    {featured.sumber} — {formatTanggal(featured.tanggal)}
                  </p>
                  <h2 className="mt-4 max-w-3xl font-serif text-3xl leading-snug text-[#F5F5F5] transition-colors group-hover:text-gold-light lg:text-4xl">
                    {featured.judul}
                  </h2>
                  {featured.ringkasan && (
                    <p className="mt-4 max-w-2xl text-sm font-light leading-relaxed text-[#A3A3A3]">
                      {featured.ringkasan}
                    </p>
                  )}
                  <span className="mt-6 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.25em] text-gold">
                    Baca di sumber
                    <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </a>
              </FadeUp>
            )}

            <div className="mt-6 divide-y divide-white/10 border border-white/10 bg-[#0d0d0d]">
              {rest.map((b, i) => (
                <FadeUp key={b.link} delay={Math.min(0.04 * i, 0.3)}>
                  <a
                    href={b.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`berita-item-${i}`}
                    className="group flex items-start gap-6 px-6 py-6 transition-colors duration-300 hover:bg-[#121212] lg:px-8"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-gold/80">
                        {b.sumber} — {formatTanggal(b.tanggal)}
                      </p>
                      <h3 className="mt-2 font-serif text-xl leading-snug text-[#F5F5F5] transition-colors group-hover:text-gold-light lg:text-2xl">
                        {b.judul}
                      </h3>
                    </div>
                    <ArrowUpRight
                      size={18}
                      className="mt-2 shrink-0 text-[#4d4d4d] transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold"
                    />
                  </a>
                </FadeUp>
              ))}
            </div>

            <p className="mt-8 text-center text-xs font-light text-[#737373]">
              Berita dihimpun otomatis dari Google Berita dan diperbarui berkala. Tautan menuju situs sumber berita masing-masing.
            </p>
          </>
        )}
      </section>

      <CtaSection
        testid="berita-cta"
        lines={[
          "Berita memicu pertanyaan?",
          <span key="t" className="italic text-gold">
            Konsultasikan pada kami.
          </span>,
        ]}
      />
    </>
  );
}
